import { useState, useEffect } from "react";
import { Typography } from "antd";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import ShippingAddressModal from "../SampleOrderForm/ShippingAddressModal";
import {
  getFormData,
  getRequestStatus,
  getRequestIdFromUrl,
} from "../../api/genericSamplesIntegrationApi";
import { Address as ApiAddress, FormData } from "../../api/samplesIntegrationApi";
import { getPartnerConfig, resolveDrugProducts } from "../../config/genericPartners";
import {
  DocUpdateHeader,
  DocUpdateFooter,
  LoadingState,
  ErrorState,
} from "./components/DocUpdateLayout";
import "../SampleOrderForm/AddressSelection.css";

const PREFIX = "address-selection";

interface Address {
  id: string;
  addressId: number;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  licNumber: string;
}

const AddressSelection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { partner = "" } = useParams<{ partner: string }>();
  const partnerConfig = getPartnerConfig(partner);

  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [requestID, setRequestID] = useState<string | null>(null);

  useEffect(() => {
    if (!partnerConfig) return;

    const fetchData = async () => {
      const requestIdFromUrl = getRequestIdFromUrl() || searchParams.get("requestID");
      if (!requestIdFromUrl) {
        setError("Invalid request. No request ID provided.");
        setIsLoading(false);
        return;
      }
      setRequestID(requestIdFromUrl);

      try {
        const status = await getRequestStatus(partner, requestIdFromUrl);
        if (status.otpStatus !== "verified") {
          navigate(`/${partner}/verify-phone?requestID=${requestIdFromUrl}`);
          return;
        }
        if (status.isClosed) {
          navigate(`/${partner}/form-unavailable`, {
            state: { reason: status.formStatus === "submitted" ? "submitted" : "closed" },
          });
          return;
        }

        const response = await getFormData(partner, requestIdFromUrl);
        setFormData(response.formData);
        setAddresses(
          (response.formData.addresses || []).map((addr: ApiAddress, index: number) => ({
            id: String(addr.addressId || `addr_${index}`),
            addressId: addr.addressId,
            street: addr.street + (addr.suite ? `, ${addr.suite}` : ""),
            city: addr.city,
            state: addr.state,
            zipCode: addr.zipCode,
            licNumber: addr.stateLicense?.licNumber || "",
          }))
        );
        setModalOpen(true);
      } catch (err: any) {
        console.error(`[generic][${partner}][AddressSelection] fetch failed:`, err);
        setError(err.message || "Failed to load addresses");
        if (err.message?.includes("not found") || err.message?.includes("expired")) {
          navigate(`/${partner}/form-unavailable`, { state: { reason: "expired" } });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [navigate, searchParams, partner, partnerConfig]);

  if (!partnerConfig) {
    return <Navigate to="/not-found" replace />;
  }

  const handleConfirm = (address: Address) => {
    setModalOpen(false);
    navigate(`/${partner}/sample-request-form?requestID=${requestID}`, {
      state: {
        requestID,
        selectedAddress: address,
        formData,
        generalInfo: {
          hcpName: formData?.hcp ? `${formData.hcp.firstName} ${formData.hcp.lastName}` : "",
          designation: formData?.hcp?.designation || "",
          stateLicenseNumber: address.licNumber,
          shippingStreet: address.street,
          shippingCity: `${address.city}, ${address.state} ${address.zipCode}`,
        },
        requestNumber: formData?.requestInfo?.requestNumber || "",
        orderItems: formData?.decision
          ? resolveDrugProducts(partner, formData.decision.drugName, formData.decision.products)
          : [],
      },
    });
  };

  if (isLoading) return <LoadingState prefix={PREFIX} message="Loading addresses..." />;
  if (error) return <ErrorState prefix={PREFIX} error={error} showRetry={false} />;

  return (
    <div className={`${PREFIX}-container`}>
      <DocUpdateHeader prefix={PREFIX} />

      <main className={`${PREFIX}-main`}>
        <div className={`${PREFIX}-blur`}>
          <Typography.Title className={`${PREFIX}-title`}>Sample Order Form</Typography.Title>

          <div className={`${PREFIX}-placeholder`}>
            <div className="placeholder-section">
              <div className="placeholder-title"></div>
              <div className="placeholder-cards">
                <div className="placeholder-card"></div>
                <div className="placeholder-card"></div>
                <div className="placeholder-card"></div>
              </div>
            </div>
            <div className="placeholder-section">
              <div className="placeholder-title"></div>
              <div className="placeholder-table"></div>
            </div>
            <div className="placeholder-section">
              <div className="placeholder-title"></div>
              <div className="placeholder-content"></div>
            </div>
          </div>
        </div>
      </main>

      <DocUpdateFooter prefix={PREFIX} />

      <ShippingAddressModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          navigate(-1);
        }}
        onConfirm={handleConfirm}
        addresses={addresses}
      />
    </div>
  );
};

export default AddressSelection;
